import React, { Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Typography, TextField, Card, CardHeader, Checkbox } from "@mui/material";
import Close from "@mui/icons-material/Close";
import isNil from "lodash/isNil";
import isEqual from "lodash/isEqual";
import isArray from "lodash/isArray";
import { timeConversion, restClient } from "client-app-core";
import { useSelector, useDispatch } from "react-redux";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";

import { Translate, getTranslation } from "orion-components/i18n";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { TargetingIcon } from "orion-components/SharedComponents";
import { DatePicker } from "orion-components/CBComponents";
import EventBus from "orion-components/SharedComponents/EventBus";
import * as actionCreators from "orion-components/Profiles/AlertProfile/actions";
import { processAggregates } from "../../../SharedComponents/trans-utils";

const propTypes = {
	closeArchive: PropTypes.func,
	timeFormat: PropTypes.string,
	reopenNotification: PropTypes.func.isRequired
};

const defaultProps = {
	closeArchive: null,
	timeFormat: "12-hour"
};
const datePickerLabelStyle = { color: "#838283", fontWeight: 500 };

const usePrevious = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

const ArchivedNotifications = props => {
	const { closeArchive, archive, fetching, shouldFetch, fetchNextPage, dir } = props;
	const dispatch = useDispatch();
	const [filterByDate, setFilterByDate] = useState(null);
	const timeFormat = useSelector(state => state.appState.global?.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const [notifications, setNotifications] = useState([]);
	const [checkedIds, setCheckedIds] = useState(new Set());
	const { reopenAlert, dumpArchive } = actionCreators;
	const eventLookups = useSelector(state => state.eventLookups);
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const { priorities, types } = eventLookups;
	const isFetchingRef = useRef(false);
	const filterByDateRef = useRef(null);

	const prevNotifications = usePrevious(props.notifications);

	useEffect(() => {
		if (!isEqual(prevNotifications, props.notifications)) {
			const notificationsWithAggregates = processAggregates(props.notifications);
			setNotifications(notificationsWithAggregates);
		}
	}, [props.notifications]);

	const searchNotification = e => {
		const value = e.target.value;
		const filterNotification = props.notifications.filter(notification => {
			return notification.message.toLowerCase().includes(value.toLowerCase());
		});
		const notificationsWithAggregates = processAggregates(filterNotification);
		setNotifications(notificationsWithAggregates);
	};

	const handleDateFilter = () => event => {
		const value = event && event.target && !isNil(event.target.value) ? event.target.value : event;
		setFilterByDate(value);
		filterByDateRef.current = value;
		if (value) {
			const startDate = new Date(value).setHours(0, 0, 0, 0);
			const endDate = new Date(value).setHours(23, 59, 59, 999);

			restClient.exec_get(
				`/ecosystem/api/alerts?isClosed=${true}&startDate=${startDate}&endDate=${endDate}`,
				(err, response) => {
					if (err) console.log(err);
					if (response && isArray(response)) {
						const notificationsWithAggregates = processAggregates(response);
						setNotifications(notificationsWithAggregates);
					}
				}
			);
		} else {
			const notificationsWithAggregates = processAggregates(props.notifications);
			setNotifications(notificationsWithAggregates);
		}
	};

	const handleCheckbox = (id, checked) => {
		setCheckedIds(prev => {
			const next = new Set(prev);
			if (checked) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	};

	const handleReactivate = () => {
		const ReactivateItems = notifications.filter(notification => checkedIds.has(notification.id));
		ReactivateItems.map(notification => {
			EventBus.publish("reopenAlerts", notification.id);
			dispatch(reopenAlert(notification));
		});
		dispatch(dumpArchive());
		closeArchive();
	};

	const handleCloseArchive = () => {
		dispatch(dumpArchive());
		closeArchive();
	};

	useEffect(() => {
		if (archive) {
			handleScroll();
		}
	}, []);

	useEffect(() => {
		isFetchingRef.current = fetching;
	}, [fetching]);

	const scrollEventHandler = event => {
		const element = event.target;
		const { scrollTop, clientHeight, scrollHeight } = element;
		if (
			Math.floor(scrollHeight - scrollTop) <= clientHeight && // --> If we scroll to bottom
			!isFetchingRef.current && // --> AND not already fetching
			shouldFetch &&
			!filterByDateRef.current
		) {
			// --> AND we haven't fetched the same data in the past X seconds
			fetchNextPage();
		}
	};

	const handleScroll = () => {
		const element = document.getElementById("virtualized-archived-notification-list");
		if (element) {
			element.addEventListener("scroll", scrollEventHandler);
		}
	};

	const getAlertSettingByProperty = (alert, setting, extract) => {
		const priority = priorities?.find(item => item.id === alert?.eventPriorityId);
		const type = types?.find(item => item.id === alert?.eventTypeId);

		switch (setting) {
			case "priority":
				return priority ? priority[extract] : priority;
			case "type":
				return type ? type[extract] : type;
			default:
				return;
		}
	};

	return (
		<div id="virtualized-archived-notification-list" style={{ width: "100%", overflow: "scroll" }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					backgroundColor: "#1D1D1D",
					paddingTop: 13,
					paddingBottom: 7,
					height: 44
				}}
			>
				<div
					style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
					onClick={() => handleCloseArchive()}
				>
					<i className="material-icons" style={{ marginLeft: 10, color: "#FFF" }}>
						keyboard_arrow_left
					</i>
					<div style={{ color: "#FFF", fontSize: 15, marginLeft: 9 }}>
						<Translate value="global.dock.notifications.main.alarmArchive.back" />
					</div>
				</div>
				<div style={{ color: "#fff" }}>
					<Translate value="global.dock.notifications.main.alarmArchive.title" />
				</div>
				<div>
					<Close
						style={{ cursor: "pointer", fontSize: 18, marginRight: 10 }}
						sx={{ color: "#FFF" }}
						onClick={() => handleCloseArchive()}
					/>
				</div>
			</div>
			<div style={{ padding: "12px 15px", display: "flex", flexDirection: "column" }}>
				<div className="search-field" style={{ marginRight: 29 }}>
					<i className="material-icons" style={{ margin: "10px 10px 0px 0px" }}>
						search
					</i>
					<TextField
						id="search-field"
						placeholder={getTranslation("global.dock.notifications.main.alarmArchive.search")}
						onChange={searchNotification}
						variant="standard"
						style={{
							backgroundColor: "transparent",
							flexGrow: 1
						}}
					/>
				</div>
				<div style={{ display: "flex", alignItems: "center", marginRight: 29 }}>
					<i
						className="material-icons"
						style={{ margin: "10px 10px 0px 0px", fontSize: 25, color: "#838283" }}
					>
						event
					</i>
					<div style={{ flexGrow: 1 }}>
						<DatePicker
							id="filter-date"
							key="filter-date"
							label={getTranslation("global.dock.notifications.main.alarmArchive.filterByDate")}
							value={filterByDate}
							onSubmit={handleDateFilter()}
							labelStyle={datePickerLabelStyle}
							dir={dir}
						/>
					</div>
				</div>
				<Button
					style={{
						height: 30,
						color: "#fff",
						padding: 0,
						fontSize: 12,
						margin: "32px 29px 10px 0"
					}}
					sx={{
						"&:hover": {
							backgroundColor: "#85C7EF !important"
						}
					}}
					onClick={() => {
						handleReactivate();
					}}
					variant="contained"
					color="primary"
				>
					<Translate value="global.dock.notifications.main.alarmArchive.reactivate" />
				</Button>
				<div style={{ overflow: "scroll" }}>
					{notifications &&
						notifications?.map((alert, index) => (
							<>
								<div style={{ width: "100%", display: "flex", alignItems: "center", marginLeft: 5 }}>
									<Card style={{ marginBottom: 12, borderRadius: 5, width: "100%" }}>
										<CardHeader
											sx={{
												color: "#fff",
												backgroundColor: checkedIds.has(alert.id) ? "#494D53" : "#2c2b2d",
												border: checkedIds.has(alert.id) ? "none" : "1px solid #494D53"
											}}
											avatar={
												<div
													style={{
														display: "flex",
														alignItems: "center"
													}}
												>
													{alert?.geometry ? (
														<TargetingIcon
															geometry={alert.geometry}
															defaultVisibility={true}
														/>
													) : null}
													<div
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															padding: 10,
															borderRadius: 5,
															backgroundColor:
																colors?.[
																	getAlertSettingByProperty(alert, "type", "color")
																]?.color ?? "#979798",
															height: 26,
															width: 26
														}}
													>
														<SpriteIcon
															iconName={
																getAlertSettingByProperty(alert, "type", "icon") ??
																"icon_information"
															}
															customHeight="17.5px"
															customWidth="17.5px"
														/>
													</div>
												</div>
											}
											title={alert?.message}
											titleTypographyProps={{
												style: {
													lineHeight: "1.25em",
													fontSize: "13px"
												}
											}}
											subheader={
												<Fragment>
													<div style={{ display: "flex" }}>
														<Typography style={{ fontSize: "11px" }} color="#82878C">
															{getAlertSettingByProperty(alert, "type", "name")}
														</Typography>
														{getAlertSettingByProperty(alert, "type", "name") ? (
															<Typography
																style={{ fontSize: "11px", margin: "0px 5px" }}
																color="#82878C"
															>
																|
															</Typography>
														) : null}
														<Typography style={{ fontSize: "11px" }} color="#82878C">
															{timeConversion.convertToUserTime(
																alert?.activityDate || alert?.createdDate,
																`full_${timeFormat}`,
																locale
															)}
														</Typography>
														{(alert.occurrenceCount || 1) > 1 && (
															<>
																<Typography
																	style={{ fontSize: "11px", margin: "0px 5px" }}
																	color="#82878C"
																>
																	|
																</Typography>
																<Typography
																	style={{ fontSize: "11px" }}
																	color="#82878C"
																>
																	<Translate
																		value="global.dock.notifications.main.alarmArchive.aggregateCount"
																		count={alert.occurrenceCount}
																	/>
																</Typography>
															</>
														)}
													</div>
													{alert?.occurrenceCount === alert?.ackedCount ? (
														<Typography style={{ fontSize: "12px" }} color="#82878C">
															{alert?.occurrences?.[0]?.acknowledgedByUserName ||
																alert?.occurrences?.[0]?.acknowledgedBy}
														</Typography>
													) : null}
												</Fragment>
											}
											subheaderTypographyProps={{
												component: "div",
												styles: {
													color: "#fff"
												}
											}}
										/>
									</Card>
									<Checkbox
										checked={checkedIds.has(alert.id)}
										sx={{
											color: "#494D53",
											"&.Mui-checked": {
												color: "primary"
											}
										}}
										onChange={e => handleCheckbox(alert.id, e.target.checked)}
									/>
								</div>
							</>
						))}
				</div>
			</div>
		</div>
	);
};

ArchivedNotifications.propTypes = propTypes;
ArchivedNotifications.defaultProps = defaultProps;

export default ArchivedNotifications;
